import { Suspense, lazy, useEffect, useLayoutEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigationType,
  useParams
} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import Home from "./pages/Home";
import { useShop } from "./context/ShopContext";

const SectionPage = lazy(() => import("./pages/SectionPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const OrderDetailPage = lazy(() => import("./pages/OrderDetailPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const RegisterSuccessPage = lazy(() => import("./pages/RegisterSuccessPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const CheckoutFailurePage = lazy(() => import("./pages/CheckoutFailurePage"));
const BankTransferCheckoutPage = lazy(() => import("./pages/BankTransferCheckoutPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminOssJobs = lazy(() => import("./pages/admin/AdminOssJobs"));
const AdminOssJobsProjection = lazy(() => import("./pages/admin/AdminOssJobsProjection"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAnalyticsSection = lazy(() => import("./pages/admin/AdminAnalyticsSection"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserOrders = lazy(() => import("./pages/admin/AdminUserOrders"));
const AdminDeletedProducts = lazy(() => import("./pages/admin/AdminDeletedProducts"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductSalesStats = lazy(() => import("./pages/admin/AdminProductSalesStats"));
const AdminProductSalesStatsDetail = lazy(() => import("./pages/admin/AdminProductSalesStatsDetail"));
const AdminProductNew = lazy(() => import("./pages/admin/AdminProductNew"));
const AdminProductEdit = lazy(() => import("./pages/admin/AdminProductEdit"));
const AdminProductDuplicate = lazy(() => import("./pages/admin/AdminProductDuplicate"));
const AdminProductImportExport = lazy(() => import("./pages/admin/AdminProductImportExport"));
const AdminSizeGuides = lazy(() => import("./pages/admin/AdminSizeGuides"));
const AdminSizeGuideNew = lazy(() => import("./pages/admin/AdminSizeGuideNew"));
const AdminSizeGuideEdit = lazy(() => import("./pages/admin/AdminSizeGuideEdit"));

const scrollPositions = new Map();

function getScrollKey(location) {
  return location.key || `${location.pathname}${location.search}`;
}

function getScrollUrlKey(location) {
  return `${location.pathname}${location.search}`;
}

function setRestoringScroll(isRestoring) {
  document.documentElement.classList.toggle("is-restoring-scroll", isRestoring);
}

function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const scrollKey = getScrollKey(location);
    const scrollUrlKey = getScrollUrlKey(location);
    const shouldRestoreScroll =
      navigationType === "POP" || location.state?.restoreScroll === true;
    const explicitScrollY = Number(location.state?.restoreScrollY);
    const savedScrollY = scrollPositions.has(scrollKey)
      ? scrollPositions.get(scrollKey)
      : scrollPositions.get(scrollUrlKey);
    const targetScrollY = Number.isFinite(explicitScrollY)
      ? explicitScrollY
      : savedScrollY;

    let restoreCancelled = false;
    let restoreTimer = null;

    function restoreSavedScroll(attempt = 0) {
      if (restoreCancelled) return;

      const nextScrollY = targetScrollY || 0;
      window.scrollTo({ top: nextScrollY, left: 0, behavior: "auto" });

      if (attempt >= 30 || Math.abs(window.scrollY - nextScrollY) <= 2) {
        setRestoringScroll(false);
        return;
      }

      restoreTimer = window.setTimeout(() => {
        restoreSavedScroll(attempt + 1);
      }, 80);
    }

    setRestoringScroll(shouldRestoreScroll);

    if (shouldRestoreScroll && typeof targetScrollY === "number") {
      restoreSavedScroll();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      setRestoringScroll(false);
    }

    return () => {
      restoreCancelled = true;
      if (restoreTimer) window.clearTimeout(restoreTimer);
      setRestoringScroll(false);
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      scrollPositions.set(scrollKey, currentScrollY);
      scrollPositions.set(scrollUrlKey, currentScrollY);
    };
  }, [location, navigationType]);

  return null;
}

function LegacyProductStatsRedirect() {
  const { productId } = useParams();

  return (
    <Navigate
      to={`/admin/analytics/products/${encodeURIComponent(productId || "")}`}
      replace
    />
  );
}

export default function App() {
  const { notice, clearNotice } = useShop();

  return (
    <div className="app-shell">
      <ScrollToTop />
      <Header />

      <main className="main-content">
        <Suspense fallback={<section className="placeholder-page"><p>Φόρτωση...</p></section>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/leonteios" element={<SectionPage sectionId="leonteios" />} />
          <Route path="/panionios-fa" element={<SectionPage sectionId="panionios-fa" />} />
          <Route path="/pgss" element={<SectionPage sectionId="pgss" />} />
          <Route path="/oss" element={<SectionPage sectionId="oss" />} />
          <Route path="/shinpad" element={<SectionPage sectionId="shinpad" />} />

          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/company/:slug" element={<LegalPage />} />
          <Route path="/privacy" element={<Navigate to="/company/privacy-gdpr" replace />} />
          <Route path="/terms" element={<Navigate to="/company/terms" replace />} />
          <Route path="/returns" element={<Navigate to="/company/returns-cancellations" replace />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/login" element={<CustomerLogin />} />
          <Route path="/register-success" element={<RegisterSuccessPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/failure" element={<CheckoutFailurePage />} />
          <Route path="/checkout/bank-transfer" element={<BankTransferCheckoutPage />} />

          <Route path="/admin/login" element={<CustomerLogin />} />

          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/orders"
            element={
              <ProtectedAdminRoute>
                <AdminOrders />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/orders/new"
            element={
              <ProtectedAdminRoute>
                <Navigate to="/cart" replace />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/orders/jobs"
            element={
              <ProtectedAdminRoute>
                <AdminOssJobs />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/orders/jobs/projection"
            element={
              <ProtectedAdminRoute>
                <AdminOssJobsProjection />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/orders/:id"
            element={
              <ProtectedAdminRoute>
                <AdminOrderDetail />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedAdminRoute>
                <AdminAnalytics />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/analytics/products"
            element={
              <ProtectedAdminRoute>
                <AdminProductSalesStats />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/analytics/products/:productId"
            element={
              <ProtectedAdminRoute>
                <AdminProductSalesStatsDetail />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/analytics/:sectionId"
            element={
              <ProtectedAdminRoute>
                <AdminAnalyticsSection />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedAdminRoute>
                <AdminUsers />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/users/:uid/orders"
            element={
              <ProtectedAdminRoute>
                <AdminUserOrders />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products"
            element={
              <ProtectedAdminRoute>
                <AdminProducts />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/statistics"
            element={
              <ProtectedAdminRoute>
                <Navigate to="/admin/analytics/products" replace />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/statistics/:productId"
            element={
              <ProtectedAdminRoute>
                <LegacyProductStatsRedirect />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/deleted"
            element={
              <ProtectedAdminRoute>
                <AdminDeletedProducts />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/new"
            element={
              <ProtectedAdminRoute>
                <AdminProductNew />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/:id/edit"
            element={
              <ProtectedAdminRoute>
                <AdminProductEdit />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/:id/duplicate"
            element={
              <ProtectedAdminRoute>
                <AdminProductDuplicate />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/products/import-export"
            element={
              <ProtectedAdminRoute>
                <AdminProductImportExport />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/size-guides"
            element={
              <ProtectedAdminRoute>
                <AdminSizeGuides />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/size-guides/new"
            element={
              <ProtectedAdminRoute>
                <AdminSizeGuideNew />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/size-guides/:id/edit"
            element={
              <ProtectedAdminRoute>
                <AdminSizeGuideEdit />
              </ProtectedAdminRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>

      {notice && (
        <div className="shop-toast" role="status">
          <span>{notice}</span>
          <button type="button" onClick={clearNotice} aria-label="Κλείσιμο">
            ×
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}
